import { memo, useState, useCallback, useMemo } from 'react';
import { View } from 'react-native';
import { FlatList } from 'react-native';
import { RefreshControl } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { MemoizedFeedItem, MemoizedEmptyComponent } from '../SocialPosts';
import PlatformFilterChips from './PlatformFilterChips';
import { getConnectedPlatforms } from './utils/platformFilterUtils';
import apis from '@api/api';

// History Tab Component - Shows FlatList of all posts
interface HistoryTabProps {
  data: any[];
  theme: boolean;
  globalDate: string;
  refetch: () => void;
  handlePostSelect: (postId: string) => void;
  selectedPostId: string | null;
  isFetching: boolean;
  isLoading: boolean;
  userIntegrations?: Record<string, any>;
  integrationData?: any[];
}

const HistoryTab = memo(({
  data,
  theme,
  globalDate,
  refetch,
  handlePostSelect,
  selectedPostId,
  isFetching,
  isLoading,
  userIntegrations = {},
  integrationData = [],
}: HistoryTabProps) => {
  const queryClient = useQueryClient();
  
  // Get connected platforms from user integrations
  const connectedPlatforms = useMemo(() => {
    return getConnectedPlatforms(userIntegrations);
  }, [userIntegrations]);

  // State for selected platforms (multi-select)
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  // Toggle platform selection
  const handlePlatformToggle = useCallback((platform: string) => {
    setSelectedPlatforms(prev => {
      if (prev.includes(platform)) {
        return prev.filter(p => p !== platform);
      } else {
        return [...prev, platform];
      }
    });
  }, []);

  // Fetch posts with platform filter
  const { data: filteredData, isFetching: isFiltering, isLoading: isFilterLoading, refetch: refetchFiltered } = useQuery({
    queryKey: ['userPosts', selectedPlatforms.length > 0 ? selectedPlatforms.sort().join(',') : 'all'],
    queryFn: async () => {
      const platforms = selectedPlatforms.length > 0 ? selectedPlatforms : undefined;
      return await apis.getUserPosts(platforms).catch(() => []);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    enabled: true, // Always enabled, but will use different query key based on selectedPlatforms
  });

  // Use filtered data if platforms are selected, otherwise use original data
  const displayData = useMemo(() => {
    if (selectedPlatforms.length > 0) {
      return filteredData || [];
    }
    return data || [];
  }, [selectedPlatforms.length, filteredData, data]);

  // Combined loading state
  const combinedIsLoading = isLoading || isFilterLoading;
  const combinedIsFetching = isFetching || isFiltering;

  // Combined refetch function
  const handleRefetch = useCallback(() => {
    refetch();
    refetchFiltered();
  }, [refetch, refetchFiltered]);

  return (
    <View style={{ flex: 1 }}>
      {/* Platform Filter Chips */}
      {connectedPlatforms.length > 0 && (
        <PlatformFilterChips
          platforms={connectedPlatforms}
          selectedPlatforms={selectedPlatforms}
          onPlatformToggle={handlePlatformToggle}
          integrationData={integrationData}
          theme={theme}
        />
      )}

      <FlatList
        data={displayData}
        keyExtractor={item => (item.id || item._id)?.toString()}
        renderItem={({ item, index }) => (
          <MemoizedFeedItem
            item={item}
            index={index}
            theme={theme}
            globalDate={globalDate}
            refetch={handleRefetch}
            onPostSelect={handlePostSelect}
            isSelected={selectedPostId === (item.id || item._id)}
          />
        )}
        contentContainerStyle={{
          gap: 15,
          paddingTop: 10,
          paddingBottom: 150,
          paddingHorizontal: 20,
          flexGrow: (!displayData || displayData.length === 0) && !combinedIsLoading && !combinedIsFetching ? 1 : 0
        }}
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <MemoizedEmptyComponent
            isLoading={combinedIsLoading}
            isFetching={combinedIsFetching}
          />
        }
        refreshControl={
          <RefreshControl
            refreshing={combinedIsFetching || combinedIsLoading}
            onRefresh={handleRefetch}
          />
        }
      />
    </View>
  );
});
export default HistoryTab;